import type { ReactNode } from 'react';
import { StoreButtons } from '../components/StoreButtons';
import { site } from '../config/site';
import { useReveal } from '../hooks/useReveal';

function Reveal({
  className = '',
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const ref = useReveal<HTMLDivElement>();
  return (
    <div ref={ref} className={`reveal ${className}`.trim()}>
      {children}
    </div>
  );
}

function HeroSky() {
  return (
    <div className="lp-sky" aria-hidden>
      <div className="lp-sky__aurora lp-sky__aurora--a" />
      <div className="lp-sky__aurora lp-sky__aurora--b" />
      <div className="lp-sky__aurora lp-sky__aurora--c" />
      <svg className="lp-sky__orbits" viewBox="0 0 1000 1000" preserveAspectRatio="xMidYMid slice">
        <circle className="lp-sky__ring lp-sky__ring--1" cx="720" cy="380" r="220" />
        <circle className="lp-sky__ring lp-sky__ring--2" cx="720" cy="380" r="320" />
        <circle className="lp-sky__ring lp-sky__ring--3" cx="720" cy="380" r="430" />
        <g className="lp-sky__dots">
          <circle cx="180" cy="160" r="1.6" />
          <circle cx="260" cy="90" r="1.1" />
          <circle cx="420" cy="140" r="1.4" />
          <circle cx="540" cy="70" r="1" />
          <circle cx="880" cy="120" r="1.3" />
          <circle cx="940" cy="280" r="1.1" />
          <circle cx="120" cy="420" r="1.2" />
          <circle cx="300" cy="520" r="1" />
          <circle cx="80" cy="680" r="1.4" />
          <circle cx="480" cy="760" r="1.1" />
          <circle cx="860" cy="700" r="1.5" />
          <circle cx="960" cy="540" r="1" />
        </g>
      </svg>
      <div className="lp-sky__mark">
        <img src={site.logoUrl} alt="" width={280} height={360} />
      </div>
    </div>
  );
}

export function LandingPage() {
  return (
    <>
      <section className="lp-hero" id="download">
        <HeroSky />
        <div className="wrap lp-hero__inner">
          <div className="lp-hero__copy">
            <p className="lp-hero__brand">
              <img src={site.logoUrl} alt="" width={44} height={56} />
              <span>MindHeart</span>
            </p>
            <h1>Know what to say next.</h1>
            <p className="lp-hero__lead">
              Compatibility insight and a coach that already knows the context —
              so your next text lands.
            </p>
            <div className="lp-hero__cta">
              <StoreButtons />
              <p className="lp-hero__note">Free to try · Premium when you go deep</p>
            </div>
          </div>
        </div>
        <a className="lp-hero__scroll" href="#glimpse" aria-label="See the app">
          <span />
        </a>
      </section>

      <section id="glimpse" className="lp-glimpse">
        <div className="lp-glimpse__plane">
          <img
            src="/screenshots/home.png"
            alt="MindHeart home — compatibility focus with your circle"
            className="lp-glimpse__shot lp-glimpse__shot--main"
          />
          <img
            src="/screenshots/coach.png"
            alt="MindHeart coach — ask what to say next about someone"
            className="lp-glimpse__shot lp-glimpse__shot--side"
          />
          <div className="lp-glimpse__veil" aria-hidden />
        </div>
        <div className="wrap lp-glimpse__copy">
          <Reveal>
            <h2>Your private circle, read clearly.</h2>
            <p>
              Add the people who matter. See how you connect. Ask what to say —
              without chart soup.
            </p>
          </Reveal>
        </div>
      </section>

      <section id="shots" className="lp-section gallery-section">
        <div className="wrap">
          <Reveal>
            <h2 className="lp-title">See it in the app.</h2>
            <p className="lp-sub section-lead">
              Home, chart, connections, reports, and coach — midnight panels,
              ice accents.
            </p>
          </Reveal>
          <div className="gallery-rail" aria-label="MindHeart App Store screenshots">
            {(
              [
                { src: '/screenshots/store-1.png', label: 'Home' },
                { src: '/screenshots/store-2.png', label: 'Chart' },
                { src: '/screenshots/store-3.png', label: 'Connections' },
                { src: '/screenshots/store-4.png', label: 'Report' },
                { src: '/screenshots/store-5.png', label: 'Coach' },
              ] as const
            ).map((shot) => (
              <figure key={shot.src} className="gallery-card">
                <img src={shot.src} alt={`MindHeart — ${shot.label}`} loading="lazy" />
              </figure>
            ))}
          </div>
        </div>
      </section>

      <section id="loop" className="lp-section">
        <div className="wrap">
          <Reveal>
            <h2 className="lp-title">Three steps. One clearer conversation.</h2>
            <p className="lp-sub">
              Add someone. Read the report. Ask the coach — grounded in that
              connection.
            </p>
          </Reveal>
          <ol className="lp-flow">
            {(
              [
                {
                  n: '01',
                  title: 'Add a person',
                  body: 'Nickname, relationship, birthday — optional place and time for sharper rising & depth.',
                },
                {
                  n: '02',
                  title: 'Read the report',
                  body: 'Communication, trust, attraction, conflict — language you can use tonight.',
                },
                {
                  n: '03',
                  title: 'Ask the coach',
                  body: '“Should I text?” “Why do we argue?” — answers that remember who you mean.',
                },
              ] as const
            ).map((s, i) => (
              <Reveal key={s.n} className={`reveal-delay-${i + 1}`}>
                <li className="lp-flow__item">
                  <span className="lp-flow__n">{s.n}</span>
                  <div>
                    <h3>{s.title}</h3>
                    <p>{s.body}</p>
                  </div>
                </li>
              </Reveal>
            ))}
          </ol>
        </div>
      </section>

      <section id="coach" className="lp-coach">
        <div className="wrap lp-coach__grid">
          <Reveal>
            <div className="lp-coach__copy">
              <h2 className="lp-title">Advice that knows the person.</h2>
              <p className="lp-sub">
                Not fortune-telling. Reflection and wording tips scoped to that
                connection — so you still sound like yourself.
              </p>
            </div>
          </Reveal>
          <Reveal className="reveal-delay-2">
            <div className="lp-dialogue" aria-label="Sample coach exchange">
              <p className="lp-dialogue__you">
                They’ve been quiet since our last talk. Should I text?
              </p>
              <p className="lp-dialogue__coach">
                Name the feeling first, then one open question. Soft tone — no
                pressure to resolve everything tonight.
              </p>
              <p className="lp-dialogue__meta">About Alex · report in context</p>
            </div>
          </Reveal>
        </div>
      </section>

      <section id="plans" className="lp-section">
        <div className="wrap">
          <Reveal>
            <h2 className="lp-title">Start free. Go deeper when it clicks.</h2>
            <p className="lp-sub">
              3-day Premium trial on signup (one per device). After that, a free
              teaser stays — upgrade when you want the full circle.
            </p>
          </Reveal>
          <div className="lp-plans">
            <Reveal>
              <a className="lp-plan" href="#download">
                <span className="lp-plan__label">Try</span>
                <h3>Free</h3>
                <p className="lp-plan__price">
                  $0 <span>· 3-day trial</span>
                </p>
                <ul>
                  <li>3 days of Premium on signup</li>
                  <li>Then 1 connection · 1 report</li>
                  <li>5 coach messages forever free</li>
                </ul>
              </a>
            </Reveal>
            <Reveal className="reveal-delay-1">
              <a className="lp-plan lp-plan--featured" href="#download">
                <span className="lp-plan__label">Popular</span>
                <h3>Premium</h3>
                <p className="lp-plan__price">
                  $9.99 <span>/ month</span>
                </p>
                <ul>
                  <li>Unlimited connections &amp; reports</li>
                  <li>Full coach history</li>
                  <li>10 min coaching / day</li>
                </ul>
              </a>
            </Reveal>
            <Reveal className="reveal-delay-2">
              <a className="lp-plan" href="#download">
                <span className="lp-plan__label">Power</span>
                <h3>Pro</h3>
                <p className="lp-plan__price">
                  $19.99 <span>/ month</span>
                </p>
                <ul>
                  <li>Everything in Premium</li>
                  <li>30 min coaching / day</li>
                  <li>Best for a busy circle</li>
                </ul>
              </a>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="lp-close">
        <div className="wrap">
          <Reveal>
            <div className="lp-close__inner">
              <img src={site.logoUrl} alt="" width={64} height={82} />
              <h2>MindHeart</h2>
              <p>Understand someone better before you hit send.</p>
              <StoreButtons />
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
